import firebase from 'firebase/app'

import { PlaybookApplicationType } from 'app/types'

import { host, uuid } from './utils'

const playbookRef = (id: string) => firebase.firestore().doc(`playbooks/${id}`)
export const getUploadRef = (uid: string) => firebase.storage().ref(`uploads/${uid}`)

const fetchStatus = (url: string, requestData: any): Promise<boolean> =>
    fetch(`${host}${url}`, requestData).then(res => res.ok)

type BatchUploadFromGdriveProps = {
    uploadedBy: UploadedByType
    uid: string
    batchId: string
    // @TODO: set normal type here when somebody will work with this function
    files: Array<unknown>
    auth?: {
        access_token: string
    }
}

type UploadedByType = {
    displayName: string
    photoURL: string
    email: string
}

export const batchUploadFromGdrive = ({
    uploadedBy,
    uid,
    batchId,
    files,
    auth
}: BatchUploadFromGdriveProps) => {
    const db = firebase.database()
    return db
        .ref(`/batchGDriveUpload/${uid}/${batchId}/data`)
        .set({ uploadedBy, uid, batchId, files, auth })
        .catch(err => console.error('uploadFromGdrive', err))
}
type UploadFromGdriveProps = {
    uploadedBy: UploadedByType
    uid: string
    uploadId: string
    // @TODO: set normal type here when somebody will work with this function
    file: {
        id: string
    }
    auth: {
        code: string
    }
}

export type DriveType = 'gdrive' | 'onedrive' | 'local' | 'zoom' | 'youtube'

export const uploadFromDrive = (data: UploadFromGdriveProps, drive: DriveType = 'gdrive') => {
    const { uid, file } = data
    if (!data.uploadedBy?.photoURL) data.uploadedBy.photoURL = ''

    return firebase
        .database()
        .ref(`${drive}/${uid}/${file.id}`)
        .set({ data, progress: -1 })
        .catch(err => console.error('uploadFromDrive', err))
}

type BatchUploadType = {
    id?: string
    uploadedBy: string
    fileList: Array<unknown>
    drive: DriveType
    isPlaylists?: boolean
}
// fileList = array of the ids
// drive - gDrive, zoom, local, oneDrive, youtube
export const batchUpload = ({
    id = uuid(),
    uploadedBy,
    fileList,
    drive,
    isPlaylists
}: BatchUploadType) => {
    return firebase
        .database()
        .ref(`batchUpload/${uploadedBy}/${id}`)
        .set({ fileList, drive, ...(isPlaylists && { isPlaylists }) })
        .catch(console.error)
}

type MovePlaybookToLibType = {
    playbookId: string
    uid: string
}

export const movePlaybookToLib = ({ playbookId, uid }: MovePlaybookToLibType) =>
    firebase
        .database()
        .ref(`playbooks/${uid}/${playbookId}/enrich`)
        .set(firebase.database.ServerValue.TIMESTAMP)

type UpdateTitle = {
    playbookId: string
    title: string
}

export const updateTitle = ({ playbookId, title }: UpdateTitle) =>
    playbookRef(playbookId).update({ title }).catch(console.error)

type UpdateComments = {
    playbookId: string
    commentsStatus: string
}

export const updateComments = ({ playbookId, commentsStatus }: UpdateComments) =>
    playbookRef(playbookId).update({ commentsStatus }).catch(console.error)

type FileType = {
    id: string
    name: string
    uid: string
    url: string
}

export const updateFiles = (playbookId: string, files: Array<FileType>) => {
    return playbookRef(playbookId).update({ files }).catch(console.error)
}

type UpdateLinksType = Omit<FileType, 'name'> & { name: string | null }

export const updateLinks = (playbookId: string, links: Array<UpdateLinksType>) => {
    return playbookRef(playbookId).update({ links }).catch(console.error)
}

type UpdateDescription = {
    playbookId: string
    description: string
}
export const updateDescription = ({ playbookId, description }: UpdateDescription) =>
    playbookRef(playbookId).update({ description }).catch(console.error)

type UpdateLinkType = {
    playbookId: string
    link: string
}
export const updateLink = ({ playbookId, link }: UpdateLinkType) =>
    playbookRef(playbookId).update({ link }).catch(console.error)

export const deletePlaybook = (playbookId: string) =>
    playbookRef(playbookId).delete().catch(console.error)

type UpdatePlayListType = {
    playbookId: string
    playbookList: Array<string>
    applications?: Array<PlaybookApplicationType>
}

export const updatePlayList = ({ playbookId, playbookList, applications }: UpdatePlayListType) => {
    return playbookRef(playbookId)
        .update({
            playbookList,
            ...(applications && { applications })
        })
        .catch(console.error)
}

// PROPS DESCRIPTION:

// data => { id, playbook, user, playList }
// Data is an object with:

// 1. id - can be created with uuid()

// 2. user should have fields: displayName, photoURL, email, uid

// 3.* playbook should have next fields: screenshotUrl, id or playbookId,
// screenshotUrl, tags

// 3. data should have: title, visibility

type CreatePlaylistWithItemsType = {
    id: string
    gifUrl: string
    screenshotUrl: string
    uploadedBy: {
        displayName: string
        photoURL: string
        email: string
    }
    uploadedByOrgId: string
    uploadedByUid: string
    isPlaylist: boolean
    playbookList: Array<string>
    title: string
    tags: Array<string>
    applications: Array<PlaybookApplicationType>
}

export const createPlayListWithItems = (data: CreatePlaylistWithItemsType) => {
    return playbookRef(data.id)
        .set({
            ...data,
            uploadedAt: firebase.firestore.FieldValue.serverTimestamp()
        })
        .catch(console.error)
}

type UploadPlaybookDoneType = {
    token: string
    playbookId: string
    originalFileName: string
    uid: string
    type: 'manual' | 'webapp'
    uploadedBy: { displayName: string; photoURL: string; email: string }
    isRecording: boolean
    duration: number
    mimeType?: string
    fileSize?: number
    url: string
}
export const uploadPlaybookDone = async ({
    token,
    playbookId,
    originalFileName,
    uid,
    type = 'manual',
    uploadedBy,
    isRecording,
    duration,
    mimeType = '',
    fileSize,
    url
}: UploadPlaybookDoneType) => {
    const title = originalFileName
        .replace(/.mp4$/i, '')
        .replace(/.webm$/i, '')
        .replace(/.mov$/i, '')

    try {
        return fetchStatus('/b/v1/playbookAction', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                authorization: `Bearer ${token}`
            },
            body: JSON.stringify({
                action: 'uploaded',
                playbookId,
                uid,
                data: {
                    type,
                    title,
                    url,
                    isRecording,
                    duration: isRecording ? duration / 1000 : 0,
                    originalFileName,
                    id: playbookId,
                    uploadedByUid: uid,
                    uploadedBy,
                    mimeType,
                    fileSize
                }
            })
        })
    } catch (e) {
        return e
    }
}

type UpdateCoverType = {
    playbookId: string
    previousScreenshotUrl: string
    previousGifUrl: string
    newCoverUrl: string
}
export const updateCover = ({
    playbookId,
    previousScreenshotUrl,
    previousGifUrl,
    newCoverUrl
}: UpdateCoverType) => {
    return playbookRef(playbookId)
        .update({
            screenshotUrl: newCoverUrl || previousScreenshotUrl,
            gifUrl: newCoverUrl || previousGifUrl,
            previousScreenshotUrl: newCoverUrl ? previousScreenshotUrl : null,
            previousGifUrl: newCoverUrl ? previousGifUrl : null
        })
        .catch(console.error)
}

export const getPlaybookStorage = (orgId: string, uid: string, id: string) => {
    return firebase.storage().ref(`playbookInfo/${orgId}/${uid}/${id}`)
}

type SetPlaybookColorType = {
    playbookId: string
    coverColor: string
}
export const updatePlaybookCoverColor = ({ playbookId, coverColor }: SetPlaybookColorType) => {
    return playbookRef(playbookId).update({ coverColor }).catch(console.error)
}
